import {
	Badge,
	Button,
	ButtonProps,
	Flex,
	Menu,
	MenuDropdownProps,
	MenuProps,
	Text,
	rem,
	FloatingPosition,
} from '@mantine/core';
import React, { ComponentPropsWithoutRef, ElementType, useEffect, useState } from 'react';

interface Props<C extends ElementType = typeof Badge> extends MenuProps {
	tags: string[];
	visibleTagCount: number;
	dropdownPosition?: FloatingPosition;
	BadgeElement?: ElementType;
	badgeProps?: ComponentPropsWithoutRef<C>;
	targetButtonProps?: ButtonProps;
	menuDropdownProps?: MenuDropdownProps;
}

export const TagsWithDropdown = <C extends ElementType>({
	tags,
	visibleTagCount,
	dropdownPosition = 'bottom-end',
	BadgeElement,
	badgeProps,
	targetButtonProps,
	menuDropdownProps,
	...rest
}: Props<C>) => {
	const [visibleItems, setVisibleItems] = useState<Props['tags']>([]);
	const [dropdownItems, setDropdownItems] = useState<Props['tags']>([]);
	const [opened, setOpen] = useState(false);

	const renderBadge = (tag: string, key: string) => {
		if (BadgeElement) {
			return (
				<BadgeElement key={key} {...badgeProps}>
					{tag}
				</BadgeElement>
			);
		}

		return (
			<Badge key={key} size="md" variant="outline" c="dark.8" color="gray">
				<Text fz="sm" fw={400}>
					{tag}
				</Text>
			</Badge>
		);
	};

	useEffect(() => {
		if (visibleTagCount === 0) {
			setVisibleItems([]);
			setDropdownItems(tags);
			return;
		} else if (!visibleTagCount) {
			setVisibleItems(tags);
			setDropdownItems([]);
			return;
		}

		setVisibleItems(tags.slice(0, visibleTagCount));
		setDropdownItems(tags.slice(visibleTagCount));
	}, [tags, visibleTagCount]);

	return tags?.length ? (
		<Flex gap="0.25rem" wrap="wrap" align="center">
			{visibleItems.map((tag, index) => renderBadge(tag, `visible-${index}`))}

			{dropdownItems?.length ? (
				<Menu
					position={dropdownPosition}
					opened={opened}
					onClose={() => setOpen(false)}
					onOpen={() => setOpen(true)}
					{...rest}
				>
					<Menu.Target>
						<Button
							variant="light"
							size="l"
							bg="gray.4"
							c="dark.8"
							radius="xl"
							p="0.25rem  0.5rem"
							sx={(theme) => ({
								'&:hover': {
									backgroundColor: theme.colors.gray[4],
								},
							})}
							{...targetButtonProps}
						>
							<Text fw={400} fz="sm">
								+{dropdownItems.length}
							</Text>
						</Button>
					</Menu.Target>

					<Menu.Dropdown
						p="0.25rem"
						sx={() => ({
							border: 'none',
							borderRadius: rem('8px'),
							boxShadow: `0px 16px 32px 0px rgba(220, 223, 224, 1)`,
						})}
						{...menuDropdownProps}
					>
						{dropdownItems?.map((tag, index) => (
							<Menu.Item p="0.25rem" key={tag} disabled>
								{renderBadge(tag, `dropdown-${index}`)}
							</Menu.Item>
						))}
					</Menu.Dropdown>
				</Menu>
			) : null}
		</Flex>
	) : null;
};
